import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { 
    User, 
    Bell,
    Shield,
    Moon,
    LogOut,
    Mail,
    Building,
    Sliders,
    Truck,
    Clock,
    DollarSign,
    AlertTriangle,
    Save,
    RotateCcw,
    Loader2,
    Brain,
    Sparkles,
    FileText,
    MessageSquare,
    BarChart3,
    Lock,
    Terminal,
    ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import IntegrationRegistry from '../components/IntegrationRegistry';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SettingsPage() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [hasChanges, setHasChanges] = useState(false);
    const [originalSettings, setOriginalSettings] = useState(null);

    // Fetch settings on mount
    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await fetch(`${API_URL}/api/settings`, {
                credentials: 'include'
            });
            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                setOriginalSettings(data);
            }
        } catch (error) {
            console.error('Failed to fetch settings:', error);
            toast.error('Failed to load settings');
        } finally {
            setLoading(false);
        }
    };

    const handleSettingChange = (field, value) => {
        const newSettings = { ...settings, [field]: value };
        setSettings(newSettings);
        
        // Check if there are changes
        const changed = Object.keys(originalSettings).some(
            key => newSettings[key] !== originalSettings[key]
        );
        setHasChanges(changed);
    };

    // Immediately save system_mode changes
    const handleSystemModeChange = async (newMode) => {
        const previousMode = settings?.system_mode;
        setSettings({ ...settings, system_mode: newMode });
        
        try {
            const response = await fetch(`${API_URL}/api/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ system_mode: newMode })
            });

            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                setOriginalSettings(data);
                toast.success(`System mode updated to ${newMode === 'testing' ? 'Testing' : 'Production'}`);
                // Dispatch event to update banner immediately in AppLayout
                window.dispatchEvent(new CustomEvent('systemModeChanged', { 
                    detail: { system_mode: newMode } 
                }));
            } else {
                // Revert on failure
                setSettings({ ...settings, system_mode: previousMode });
                toast.error('Failed to update system mode');
            }
        } catch (error) {
            console.error('Failed to update system mode:', error);
            setSettings({ ...settings, system_mode: previousMode });
            toast.error('Failed to update system mode');
        }
    };

    const handleSaveSettings = async () => {
        setSaving(true);
        try {
            // Only send changed fields
            const changedFields = {};
            Object.keys(settings).forEach(key => {
                if (settings[key] !== originalSettings[key]) {
                    changedFields[key] = settings[key];
                }
            });

            const response = await fetch(`${API_URL}/api/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(changedFields)
            });

            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                setOriginalSettings(data);
                setHasChanges(false);
                toast.success('Settings saved successfully');
            } else {
                throw new Error('Failed to save');
            }
        } catch (error) {
            console.error('Failed to save settings:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleResetToDefaults = async () => {
        const defaults = {
            min_broker_credit_score: 90,
            max_broker_days_to_pay: 30,
            critical_invoice_days: 45,
            warning_invoice_days: 30,
            default_dispatch_percent: 7.0,
            default_tax_percent: 25.0,
            default_savings_percent: 10.0
        };

        setSaving(true);
        try {
            const response = await fetch(`${API_URL}/api/settings`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(defaults)
            });

            if (response.ok) {
                const data = await response.json();
                setSettings(data);
                setOriginalSettings(data);
                setHasChanges(false);
                toast.success('Settings reset to defaults');
            }
        } catch (error) {
            console.error('Failed to reset settings:', error);
            toast.error('Failed to reset settings');
        } finally {
            setSaving(false);
        }
    };

    const handleDiscardChanges = () => {
        setSettings({ ...originalSettings });
        setHasChanges(false);
    };

    const getInitials = (name) => {
        if (!name) return 'U';
        return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
    };

    const handleLogout = async () => {
        await logout();
    };

    // Number input component with validation
    const NumberInput = ({ label, description, field, min, max, step = 1, suffix = '' }) => (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
            <div>
                <Label className="text-white text-sm">{label}</Label>
                <p className="text-zinc-500 text-xs mt-1">{description}</p>
            </div>
            <div className="flex items-center gap-2">
                <Input
                    type="number"
                    min={min}
                    max={max}
                    step={step}
                    value={settings?.[field] ?? ''}
                    onChange={(e) => {
                        const val = step === 1 ? parseInt(e.target.value) : parseFloat(e.target.value);
                        if (!isNaN(val)) {
                            handleSettingChange(field, val);
                        }
                    }}
                    className="bg-zinc-800 border-zinc-700 text-white w-28 text-right"
                    data-testid={`input-${field}`}
                />
                {suffix && <span className="text-zinc-400 text-sm">{suffix}</span>}
            </div>
        </div>
    );

    // Toggle input component for boolean settings
    const ToggleInput = ({ label, description, field, compact = false }) => (
        <div className={`flex items-center justify-between ${compact ? '' : 'gap-4'}`}>
            <div className={compact ? '' : 'flex-1'}>
                <Label className="text-white text-sm">{label}</Label>
                {description && <p className="text-zinc-500 text-xs mt-1">{description}</p>}
            </div>
            <Switch
                checked={settings?.[field] ?? false}
                onCheckedChange={(checked) => handleSettingChange(field, checked)}
                data-testid={`toggle-${field}`}
            />
        </div>
    );

    // AI Feature toggle with disabled state badge
    const AIFeatureToggle = ({ label, description, field, settings, onChange, disabled }) => (
        <div className="flex items-center justify-between gap-4">
            <div className="flex-1">
                <div className="flex items-center gap-2">
                    <Label className="text-white text-sm">{label}</Label>
                    {disabled && (
                        <Badge variant="outline" className="text-zinc-500 border-zinc-600 text-xs">
                            Enable AI to use
                        </Badge>
                    )}
                </div>
                {description && <p className="text-zinc-500 text-xs mt-1">{description}</p>}
            </div>
            <Switch
                checked={settings?.[field] ?? false}
                onCheckedChange={(checked) => onChange(field, checked)}
                disabled={disabled}
                data-testid={`toggle-${field}`}
            />
        </div>
    );

    return (
        <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="settings-page">
            {/* Page Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-white tracking-tight uppercase">Settings</h1>
                    <p className="text-zinc-500 text-sm mt-1">Manage your account and system policies</p>
                </div>
                {hasChanges && (
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={handleDiscardChanges}
                            className="border-zinc-700 text-zinc-400"
                            data-testid="discard-changes-btn"
                        >
                            Discard
                        </Button>
                        <Button
                            size="sm"
                            onClick={handleSaveSettings}
                            disabled={saving}
                            className="bg-orange-500 hover:bg-orange-600 text-white"
                            data-testid="save-settings-btn"
                        >
                            {saving ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Save className="w-4 h-4 mr-2" />
                            )}
                            Save Changes
                        </Button>
                    </div>
                )}
            </div>

            {/* System Policies Section */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-orange-500">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                                <Sliders className="w-5 h-5 text-orange-500" />
                                System Policies
                            </CardTitle>
                            <CardDescription className="text-zinc-500 mt-1">
                                Configure business logic thresholds for the dispatch system
                            </CardDescription>
                        </div>
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleResetToDefaults}
                            disabled={saving}
                            className="text-zinc-400 hover:text-white"
                            data-testid="reset-defaults-btn"
                        >
                            <RotateCcw className="w-4 h-4 mr-2" />
                            Reset Defaults
                        </Button>
                    </div>
                </CardHeader>
                <CardContent className="p-6 space-y-8">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-orange-500 animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Broker Policy Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-orange-400 mb-2">
                                    <Shield className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Broker Policy Thresholds</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <NumberInput
                                        label="Minimum Credit Score"
                                        description="Brokers below this score cannot be used for load staging"
                                        field="min_broker_credit_score"
                                        min={0}
                                        max={100}
                                        suffix="points"
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <NumberInput
                                        label="Maximum Days to Pay"
                                        description="Brokers exceeding this payment term are blocked"
                                        field="max_broker_days_to_pay"
                                        min={1}
                                        max={120}
                                        suffix="days"
                                    />
                                </div>
                            </div>

                            {/* Receivables Protection Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-amber-400 mb-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Receivables Protection</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <NumberInput
                                        label="Warning Invoice Days"
                                        description="Invoices past this age appear in amber (warning) bucket"
                                        field="warning_invoice_days"
                                        min={15}
                                        max={60}
                                        suffix="days"
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <NumberInput
                                        label="Critical Invoice Days"
                                        description="Invoices past this age trigger critical alerts"
                                        field="critical_invoice_days"
                                        min={30}
                                        max={90}
                                        suffix="days"
                                    />
                                </div>
                            </div>

                            {/* Financial Defaults Section */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-green-400 mb-2">
                                    <DollarSign className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Financial Defaults</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <NumberInput
                                        label="Default Dispatch %"
                                        description="Default dispatch fee percentage for new loads"
                                        field="default_dispatch_percent"
                                        min={1}
                                        max={20}
                                        step={0.5}
                                        suffix="%"
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <NumberInput
                                        label="Default Tax %"
                                        description="Default tax reserve percentage for accounting"
                                        field="default_tax_percent"
                                        min={0}
                                        max={50}
                                        step={0.5}
                                        suffix="%"
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <NumberInput
                                        label="Default Savings %"
                                        description="Default savings allocation percentage"
                                        field="default_savings_percent"
                                        min={0}
                                        max={30}
                                        step={0.5}
                                        suffix="%"
                                    />
                                </div>
                            </div>

                            {/* Document Signing Settings */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-purple-400 mb-2">
                                    <FileText className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Document Signing</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-white text-sm font-medium">Default Signature Position</p>
                                                <p className="text-zinc-500 text-xs mt-0.5">Where to place signature on rate confirmations</p>
                                            </div>
                                        </div>
                                        <div className="flex gap-2 flex-wrap">
                                            {[
                                                { value: 'bottom-left', label: 'Bottom Left' },
                                                { value: 'bottom-center', label: 'Bottom Center' },
                                                { value: 'bottom-right', label: 'Bottom Right' }
                                            ].map(opt => (
                                                <Button
                                                    key={opt.value}
                                                    variant={settings?.default_signature_position === opt.value ? 'default' : 'outline'}
                                                    size="sm"
                                                    onClick={() => handleSettingChange('default_signature_position', opt.value)}
                                                    className={settings?.default_signature_position === opt.value 
                                                        ? 'bg-purple-600 hover:bg-purple-700' 
                                                        : 'border-zinc-600 text-zinc-300'
                                                    }
                                                >
                                                    {opt.label}
                                                </Button>
                                            ))}
                                        </div>
                                    </div>
                                    <Separator className="bg-zinc-800" />
                                    <div className="space-y-2">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-white text-sm font-medium">Signature Image</p>
                                                <p className="text-zinc-500 text-xs mt-0.5">
                                                    {settings?.user_signature_file 
                                                        ? 'Signature uploaded ✓' 
                                                        : 'Upload your signature PNG for rate confirmations'
                                                    }
                                                </p>
                                            </div>
                                            <label className="cursor-pointer">
                                                <input
                                                    type="file"
                                                    accept=".png,.jpg,.jpeg"
                                                    onChange={async (e) => {
                                                        const file = e.target.files?.[0];
                                                        if (!file) return;
                                                        
                                                        const formData = new FormData();
                                                        formData.append('file', file);
                                                        
                                                        try {
                                                            const res = await fetch(`${API_URL}/api/signature/upload`, {
                                                                method: 'POST',
                                                                credentials: 'include',
                                                                body: formData
                                                            });
                                                            if (res.ok) {
                                                                toast.success('Signature uploaded!');
                                                                fetchSettings();
                                                            } else {
                                                                toast.error('Upload failed');
                                                            }
                                                        } catch {
                                                            toast.error('Upload failed');
                                                        }
                                                    }}
                                                    className="hidden"
                                                />
                                                <Button variant="outline" size="sm" className="border-zinc-600 text-zinc-300" asChild>
                                                    <span>
                                                        <FileText className="w-3 h-3 mr-1" />
                                                        {settings?.user_signature_file ? 'Replace' : 'Upload'}
                                                    </span>
                                                </Button>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Dynamic Integration Registry */}
            <IntegrationRegistry />

            {/* System Controls - Master Toggles & Configuration */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-cyan-500">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                        <Sliders className="w-5 h-5 text-cyan-500" />
                        System Controls
                    </CardTitle>
                    <CardDescription className="text-zinc-500 mt-1">
                        Master toggles and tracking configuration. Integrations are managed in the registry above.
                    </CardDescription>
                </CardHeader>
                <CardContent className="p-6 space-y-8">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-cyan-500 animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* Master Integration Toggles */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-blue-400 mb-2">
                                    <Shield className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Master Integration Toggles</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <p className="text-zinc-500 text-xs mb-3">
                                        These master switches control whether each category of integrations is active. 
                                        Individual integrations are managed in the Integration Registry above.
                                    </p>
                                    <ToggleInput label="Load Boards" description="Enable load board integrations (DAT, Truckstop, etc.)" field="enable_load_boards" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="AI Features" description="Master switch for all AI-powered features" field="enable_ai" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="Routing" description="Enable routing API for distance calculations" field="enable_routing" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="SMS" description="Enable SMS notifications and Text-to-Track" field="enable_sms" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="ELD/GPS Tracking" description="Enable ELD API integration for GPS tracking" field="enable_eld_tracking" />
                                </div>
                            </div>

                            {/* Tracking Configuration */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-cyan-400 mb-2">
                                    <Clock className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Tracking Configuration</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <ToggleInput label="Web Ping Tracking" description="Allow drivers to update location via web" field="enable_web_ping_tracking" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="SMS Ping Tracking" description="Allow drivers to update location via SMS" field="enable_sms_ping_tracking" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="Manual Tracking Entry" description="Allow manual location updates" field="allow_manual_tracking_entry" />
                                    <Separator className="bg-zinc-800" />
                                    <NumberInput
                                        label="Tracking Interval"
                                        description="Expected update frequency for tracking"
                                        field="tracking_interval_minutes"
                                        min={5}
                                        max={60}
                                        suffix="minutes"
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <NumberInput
                                        label="Stale Cycles"
                                        description="Number of missed intervals before alert"
                                        field="tracking_stale_cycles"
                                        min={1}
                                        max={5}
                                        suffix="cycles"
                                    />
                                </div>
                            </div>

                            {/* Broker Portal */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-orange-400 mb-2">
                                    <Building className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Broker Tracking Portal</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <ToggleInput label="Allow Broker Portal" description="Enable secure tracking links for brokers" field="allow_broker_tracking_portal" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="Auto Send Tracking Link" description="Automatically send link when load is dispatched" field="auto_send_tracking_link" />
                                </div>
                            </div>

                            {/* Alerts */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-red-400 mb-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Alert Settings</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <ToggleInput label="Tracking Stale Alerts" description="Alert when tracking signal is lost" field="enable_tracking_stale_alerts" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="HOS Alerts" description="Alert for Hours of Service violations" field="enable_hos_alerts" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="Invoice Alerts" description="Alert for overdue invoices" field="enable_invoice_alerts" />
                                    <Separator className="bg-zinc-800" />
                                    <ToggleInput label="Broker Policy Alerts" description="Alert for broker policy violations" field="enable_broker_policy_alerts" />
                                    <Separator className="bg-zinc-800" />
                                    <div className="pt-2">
                                        <p className="text-zinc-400 text-sm mb-3">Alert Delivery Methods</p>
                                        <div className="grid grid-cols-3 gap-4">
                                            <ToggleInput label="In-App" description="" field="alert_in_app" compact={true} />
                                            <ToggleInput label="SMS" description="" field="alert_sms" compact={true} />
                                            <ToggleInput label="Email" description="" field="alert_email" compact={true} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* System Mode */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-yellow-400 mb-2">
                                    <Shield className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">System Mode</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">
                                        <div>
                                            <Label className="text-white text-sm">System Mode</Label>
                                            <p className="text-zinc-500 text-xs mt-1">
                                                Testing mode enables mock data injection and detailed logging
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <select
                                                value={settings?.system_mode || 'production'}
                                                onChange={(e) => handleSystemModeChange(e.target.value)}
                                                className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2 w-40"
                                                data-testid="input-system_mode"
                                            >
                                                <option value="production">Production</option>
                                                <option value="testing">Testing</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Debug Console Link - Only show for admins in testing mode */}
                                {user?.role === 'admin' && settings?.system_mode === 'testing' && (
                                    <div className="bg-red-950/30 rounded-lg p-4 border border-red-900/50">
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-center">
                                            <div>
                                                <Label className="text-red-400 text-sm flex items-center gap-2">
                                                    <Terminal className="w-4 h-4" />
                                                    Debug Console
                                                </Label>
                                                <p className="text-zinc-500 text-xs mt-1">
                                                    View captured API requests, errors, and diagnostic data
                                                </p>
                                            </div>
                                            <div className="flex justify-start md:justify-end">
                                                <Button
                                                    variant="outline"
                                                    onClick={() => navigate('/admin/debug')}
                                                    className="border-red-900 text-red-400 hover:bg-red-950 hover:text-red-300"
                                                    data-testid="debug-console-link"
                                                >
                                                    <Terminal className="w-4 h-4 mr-2" />
                                                    Open Debug Console
                                                    <ExternalLink className="w-3 h-3 ml-2" />
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* AI Control Center */}
            <Card className="bg-zinc-900/50 border-zinc-800 border-l-4 border-l-purple-500">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                                <Brain className="w-5 h-5 text-purple-500" />
                                AI Control Center
                            </CardTitle>
                            <CardDescription className="text-zinc-500 mt-1">
                                Granular control over AI features. All features optional.
                            </CardDescription>
                        </div>
                        {!settings?.enable_ai && (
                            <Badge variant="outline" className="text-amber-400 border-amber-400">
                                <Lock className="w-3 h-3 mr-1" />
                                AI Disabled
                            </Badge>
                        )}
                    </div>
                </CardHeader>
                <CardContent className="p-6 space-y-8">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 text-purple-500 animate-spin" />
                        </div>
                    ) : (
                        <>
                            {/* AI Master Control */}
                            <div className="space-y-4">
                                <div className="flex items-center gap-2 text-purple-400 mb-2">
                                    <Sparkles className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Master Control</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <Label className="text-white text-sm">Enable AI Features</Label>
                                            <p className="text-zinc-500 text-xs mt-1">
                                                Master switch. When OFF, all AI features are disabled.
                                            </p>
                                        </div>
                                        <Switch
                                            checked={settings?.enable_ai ?? false}
                                            onCheckedChange={(checked) => handleSettingChange('enable_ai', checked)}
                                            data-testid="toggle-enable_ai"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Safety Notice */}
                            <div className="bg-purple-500/10 border border-purple-500/30 rounded-lg p-4">
                                <div className="flex items-start gap-3">
                                    <Shield className="w-5 h-5 text-purple-400 mt-0.5" />
                                    <div>
                                        <p className="text-purple-300 text-sm font-semibold">AI Safety Rules</p>
                                        <p className="text-zinc-400 text-xs mt-1">
                                            AI may suggest, flag, and draft - but NEVER auto-book, auto-dispatch, sign rate confirmations, 
                                            or modify financial values. Final authority always belongs to you.
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Document Intelligence */}
                            <div className={`space-y-4 ${!settings?.enable_ai ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="flex items-center gap-2 text-blue-400 mb-2">
                                    <FileText className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Document Intelligence</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <AIFeatureToggle 
                                        label="Rate Con Extraction"
                                        description="Extract key data from rate confirmation documents"
                                        field="ai_ratecon_extraction"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <AIFeatureToggle 
                                        label="Rate Con Discrepancy Check"
                                        description="Detect discrepancies between rate con and load details"
                                        field="ai_ratecon_discrepancy_check"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <AIFeatureToggle 
                                        label="Document Summarization"
                                        description="Summarize long documents and contracts"
                                        field="ai_document_summarization"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                </div>
                            </div>

                            {/* Load & Chain Intelligence */}
                            <div className={`space-y-4 ${!settings?.enable_ai ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="flex items-center gap-2 text-green-400 mb-2">
                                    <BarChart3 className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Load & Chain Intelligence</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <AIFeatureToggle 
                                        label="Load Scoring Reasoning"
                                        description="Explain why a load received its score"
                                        field="ai_load_scoring_reasoning"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <AIFeatureToggle 
                                        label="Chain Reasoning"
                                        description="Explain optimal chain recommendations"
                                        field="ai_chain_reasoning"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                </div>
                            </div>

                            {/* Communication */}
                            <div className={`space-y-4 ${!settings?.enable_ai ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="flex items-center gap-2 text-cyan-400 mb-2">
                                    <MessageSquare className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Communication</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <AIFeatureToggle 
                                        label="Negotiation Assistant"
                                        description="Suggest negotiation strategies and talking points"
                                        field="ai_negotiation_assistant"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <AIFeatureToggle 
                                        label="Email Drafting"
                                        description="Draft professional emails to brokers"
                                        field="ai_email_drafting"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                </div>
                            </div>

                            {/* Analysis */}
                            <div className={`space-y-4 ${!settings?.enable_ai ? 'opacity-50 pointer-events-none' : ''}`}>
                                <div className="flex items-center gap-2 text-orange-400 mb-2">
                                    <AlertTriangle className="w-4 h-4" />
                                    <span className="text-sm font-semibold uppercase tracking-wider">Analysis</span>
                                </div>
                                <div className="bg-zinc-800/30 rounded-lg p-4 space-y-4">
                                    <AIFeatureToggle 
                                        label="Broker Risk Analysis"
                                        description="Analyze broker risk factors and history"
                                        field="ai_broker_risk_analysis"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                    <Separator className="bg-zinc-800" />
                                    <AIFeatureToggle 
                                        label="Operational Insights"
                                        description="Generate business insights from operational data"
                                        field="ai_operational_insights"
                                        settings={settings}
                                        onChange={handleSettingChange}
                                        disabled={!settings?.enable_ai}
                                    />
                                </div>
                            </div>
                        </>
                    )}
                </CardContent>
            </Card>

            {/* Profile Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                        <User className="w-5 h-5 text-orange-500" />
                        Profile
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex items-start gap-6">
                        <Avatar className="h-20 w-20">
                            <AvatarImage src={user?.picture} alt={user?.name} />
                            <AvatarFallback className="bg-zinc-800 text-orange-500 text-xl">
                                {getInitials(user?.name)}
                            </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 space-y-4">
                            <div>
                                <Label className="text-zinc-500 text-xs uppercase tracking-wider">Name</Label>
                                <p className="text-white text-lg mt-1">{user?.name || 'User'}</p>
                            </div>
                            <div>
                                <Label className="text-zinc-500 text-xs uppercase tracking-wider">Email</Label>
                                <p className="text-zinc-300 mt-1 flex items-center gap-2">
                                    <Mail className="w-4 h-4 text-zinc-500" />
                                    {user?.email || 'Not available'}
                                </p>
                            </div>
                            <div>
                                <Label className="text-zinc-500 text-xs uppercase tracking-wider">Role</Label>
                                <p className="text-zinc-300 mt-1 flex items-center gap-2">
                                    <Building className="w-4 h-4 text-zinc-500" />
                                    {user?.role || 'Dispatcher'}
                                </p>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* Notifications Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                        <Bell className="w-5 h-5 text-orange-500" />
                        Notifications
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">Email Notifications</p>
                            <p className="text-zinc-500 text-sm">Receive updates about your loads via email</p>
                        </div>
                        <Switch defaultChecked data-testid="email-notifications-switch" />
                    </div>
                    <Separator className="bg-zinc-800" />
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">Load Status Alerts</p>
                            <p className="text-zinc-500 text-sm">Get notified when load status changes</p>
                        </div>
                        <Switch defaultChecked data-testid="status-alerts-switch" />
                    </div>
                    <Separator className="bg-zinc-800" />
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">New Load Board Matches</p>
                            <p className="text-zinc-500 text-sm">Alerts for loads matching your preferences</p>
                        </div>
                        <Switch data-testid="load-matches-switch" />
                    </div>
                    <Separator className="bg-zinc-800" />
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">Invoice Reminders</p>
                            <p className="text-zinc-500 text-sm">Get reminders for pending invoices</p>
                        </div>
                        <Switch defaultChecked data-testid="invoice-reminders-switch" />
                    </div>
                </CardContent>
            </Card>

            {/* Appearance Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                        <Moon className="w-5 h-5 text-orange-500" />
                        Appearance
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">Dark Mode</p>
                            <p className="text-zinc-500 text-sm">Dark mode is currently enabled</p>
                        </div>
                        <Switch checked disabled data-testid="dark-mode-switch" />
                    </div>
                </CardContent>
            </Card>

            {/* Security Section */}
            <Card className="bg-zinc-900/50 border-zinc-800">
                <CardHeader className="border-b border-zinc-800 pb-4">
                    <CardTitle className="text-lg font-semibold text-white uppercase tracking-wide flex items-center gap-2">
                        <Shield className="w-5 h-5 text-orange-500" />
                        Security
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-6 space-y-6">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">Authentication Method</p>
                            <p className="text-zinc-500 text-sm">You're signed in with Google</p>
                        </div>
                        <div className="flex items-center gap-2 text-zinc-400">
                            <svg className="w-5 h-5" viewBox="0 0 24 24">
                                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                            </svg>
                            <span>Google Account</span>
                        </div>
                    </div>
                    <Separator className="bg-zinc-800" />
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-white">Sign Out</p>
                            <p className="text-zinc-500 text-sm">Sign out from your account</p>
                        </div>
                        <Button 
                            variant="outline" 
                            className="border-red-800 text-red-400 hover:bg-red-500/10"
                            onClick={handleLogout}
                            data-testid="logout-btn"
                        >
                            <LogOut className="w-4 h-4 mr-2" />
                            Sign Out
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* App Info */}
            <div className="text-center py-6">
                <p className="text-zinc-600 text-sm">
                    LoadTMS v1.0.0 • Built with Emergent
                </p>
                <p className="text-zinc-700 text-xs mt-1">
                    Load board integrations: DAT, Truckstop (MOCK)
                </p>
            </div>
        </div>
    );
}
